import React, {useState, useEffect} from 'react';
import {
  Box,
  Typography,
  Paper,
} from '@mui/material';
import { collection, collectionGroup, getDocs, doc, getDoc, query, where } from 'firebase/firestore';
import { db } from '../Firebase';
import { useTranslation } from 'react-i18next';
import CircularProgress from '@mui/material/CircularProgress';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';

function AdminAnalytics() {
  const { t } = useTranslation();
  const [selectedSiteId, setSelectedSiteId] = useState(null);
  const [loadingSites, setLoadingSites] = useState(false);
  const [loadingViolations, setLoadingViolations] = useState(false);
  const [sites, setSites] = useState([]);
  const [violationFilter, setViolationFilter] = useState('');
  const [lateUsers, setLateUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  // Time extraction helper
  const getTodayISO = () => {
    const now = new Date();
    const tzOffsetMs = now.getTimezoneOffset() * 60000;
    return new Date(now.getTime() - tzOffsetMs).toISOString().slice(0, 10);
  };

  const getTodayDMY = () => {
    const now = new Date();
    const dd = String(now.getDate()).padStart(2, "0");
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const yyyy = String(now.getFullYear());
    return `${dd}/${mm}/${yyyy}`; // "DD/MM/YYYY"
  };

  const buildTodayDateConstraints = () => {
    const iso = getTodayISO();  // "YYYY-MM-DD"
    const dmy = getTodayDMY();  // "DD/MM/YYYY"
    return iso === dmy ? [iso] : [iso, dmy];
  };

  // fetch construction sites
  useEffect(() => {
    const fetchSites = async () => {
      setLoadingSites(true);
      try {
        const snapshot = await getDocs(collection(db, 'ConstructionSites'));
        const siteList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setSites(siteList);
      } catch (error) {
        console.error('Failed to fetch construction sites:', error);
      } finally {
        setLoadingSites(false);
      }
    };
    fetchSites();
  }, []);




  // Fetch violtions and violators 
  useEffect(() => {
  const fetchViolators = async () => {
    setLoadingViolations(true); 
    try {
      const todayDates = buildTodayDateConstraints();
      const todayDay = new Date().getDay(); // 0=Sun ... 6=Sat
      const isWeekday = todayDay >= 1 && todayDay <= 5;
      const usersSnap = await getDocs(collection(db, 'users'));
      const siteMap = {};
      const sitesSnap = await getDocs(collection(db, 'ConstructionSites'));
      sitesSnap.forEach((siteDoc) => {
        const data = siteDoc.data();
        siteMap[siteDoc.id] = {
          name: data.name || '—',
          startTime: data.startTime || '08:00',
          endTime: data.endTime || '17:00',
          lat: data.location?.lat ?? null,
          lng: data.location?.lng ?? null,
        };
      });

      const clockingGroupQuery =
        todayDates.length === 1
          ? query(
              collectionGroup(db, "clocking history"),
              where("date", "==", todayDates[0])
            )
          : query(
              collectionGroup(db, "clocking history"),
              where("date", "in", todayDates)
            );
      const clockingGroupSnap = await getDocs(clockingGroupQuery);
      const clockingByUserId = new Map();
      clockingGroupSnap.forEach((docSnap) => {
        const data = docSnap.data();
        const userId = docSnap.ref.parent?.parent?.id;
        if (!userId) return;
        if (!clockingByUserId.has(userId)) {
          clockingByUserId.set(userId, []);
        }
        clockingByUserId.get(userId).push(data);
      });
      const newViolators = [];

      for (const userDoc of usersSnap.docs) {
        const userData = userDoc.data();
        if (userData.role !== 'worker') continue;

        const siteId = userData.assignedSiteId || null;

        // Fetch site name if not already cached
        let siteName = '—';
        if (siteId) {
          siteName = siteMap[siteId]?.name || 'Unknown Site';
        }

        const entries = clockingByUserId.get(userDoc.id) || [];
        const normalizeType = (s) =>
          String(s || "")
            .toLowerCase()
            .replace(/[\s-_]/g, "");

        const pickClockEntry = (entries, wanted) => {
          const w = normalizeType(wanted); // "clockin" / "clockout"
          return entries.find((e) => normalizeType(e.type) === w) || null;
        };

        const clockIn = pickClockEntry(entries, "Clock In");
        const clockOut = pickClockEntry(entries, "Clock Out");

        let siteStart = '08:00';
        let siteEnd = '17:00';

        // Site location coordinates
        let siteLat = null;
        let siteLng = null;
        if (siteId) {
          siteLat = siteMap[siteId]?.lat ?? null;
          siteLng = siteMap[siteId]?.lng ?? null;
        }

        const violations = [];

        // ABSENT (Mon-Fri only): no clocking at all during the shift
        if (isWeekday && siteId && !clockIn && !clockOut) {
          violations.push('Absent');
        }

        // Check location deviation (Clock-In)
        const distanceLimitKm = 1;
        if (clockIn && siteLat && siteLng && clockIn.locationCoords) {
          const dist = calculateDistanceKm(
            siteLat,
            siteLng,
            clockIn.locationCoords.lat,
            clockIn.locationCoords.lng
          );

          console.log('Distance from site:', dist, 'km');

          if (dist > distanceLimitKm) {
            console.log('Outside zone violation triggered');
            violations.push('Clock-In outside zone');
          }
        }

        if (siteId) {
          siteStart = siteMap[siteId]?.startTime || '08:00';
          siteEnd = siteMap[siteId]?.endTime || '17:00';
        }

        const toTimeHHMMSS = (timeStr) => {
          if (!timeStr) return null;

          const s = String(timeStr).trim();

          // Format: "06 h 45 min 56 s" (or "6 h 5 min 3 s")
          // Also tolerate missing seconds: "06 h 45 min"
          const hmsMatch = s.match(/(\d{1,2})\s*h\s*(\d{1,2})\s*min(?:\s*(\d{1,2})\s*s)?/i);
          if (hmsMatch) {
            const hh = String(hmsMatch[1]).padStart(2, "0");
            const mm = String(hmsMatch[2]).padStart(2, "0");
            const ss = String(hmsMatch[3] ?? "0").padStart(2, "0");
            return `${hh}:${mm}:${ss}`;
          }

          // Format: "06:45" or "06:45:56"
          if (!s.includes("AM") && !s.includes("PM") && s.includes(":")) {
            const parts = s.split(":").map((p) => p.trim());
            if (parts.length === 2) {
              const [hh, mm] = parts;
              return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}:00`;
            }
            if (parts.length === 3) {
              const [hh, mm, ss] = parts;
              return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
            }
          }

          // Format: "6:45 AM" / "06:45 PM"
          if (s.includes("AM") || s.includes("PM")) {
            const [time, mod] = s.split(" ");
            let [h, m] = time.split(":");
            if (h === "12") h = "00";
            if (mod === "PM") h = String(parseInt(h, 10) + 12);
            return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:00`;
          }

          return null;
        };

        if (clockIn) {
          const clockInTime = toTimeHHMMSS(clockIn.time);
          const siteStartTime = toTimeHHMMSS(siteStart);

          console.log("DEBUG late-check:", {
            worker: userData.fullName,
            rawClockIn: clockIn?.time,
            parsedClockIn: clockInTime,
            siteStart,
            parsedSiteStart: siteStartTime,
          });

          if (clockInTime && siteStartTime) {
            const clockInDate = new Date(`2000-01-01T${clockInTime}`);
            const startDate = new Date(`2000-01-01T${siteStartTime}`);
            const diffMinutes = (clockInDate - startDate) / 60000;

            if (diffMinutes > 10) violations.push("Late Clock-In");
          }
        }

        if (clockOut) {
          const clockOutTime = toTimeHHMMSS(clockOut.time); // "HH:MM:SS"
          const siteEndTime = toTimeHHMMSS(siteEnd);        // siteEnd is usually "HH:MM"

          if (clockOutTime && siteEndTime) {
            const clockOutDate = new Date(`2000-01-01T${clockOutTime}`);
            const endDate = new Date(`2000-01-01T${siteEndTime}`);
            const diffMinutes = (clockOutDate - endDate) / 60000;

            // If they clock out AFTER end time by > 10 minutes
            if (diffMinutes > 10) {
              violations.push("Late Clock-Out");
            }
            // If they clock out BEFORE end time (any amount)
            else if (diffMinutes < 0) {
              violations.push("Early Clock-Out");
            }
          }
        }

        if (violations.length > 0) {
          newViolators.push({
            name: userData.fullName,
            violations,
            siteName,
            siteId
          });
         }
        }

      setLateUsers(newViolators);
    } catch (error) {
      console.error('Failed to fetch violations:', error);
    } finally {
      setLoadingViolations(false); // ✅ End spinner
    }
  };

    fetchViolators();
  }, []);


  // Calculate distance between clocking user AND construction site
  function calculateDistanceKm(lat1, lon1, lat2, lon2) {
    const R = 6371; // Earth radius in KM
    const toRad = (x) => (x * Math.PI) / 180;

    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(lat1)) *
        Math.cos(toRad(lat2)) *
        Math.sin(dLon / 2) ** 2;

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }


  return (

    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

      {/* top Panel */}
      <Box
        sx={{
          flexGrow: 1,
          p: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        {/* Header / Empty Activity State */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            backgroundColor: '#ffffff',
            textAlign: 'center',
            borderRadius: 2,
            boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
            marginBottom: '20px'
          }}
        >
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            {t('Welcome to SofBen Analytics Page:')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('The list below will display all wokers who either clocked in late, clocked in outside work zone or clock out late.')}
          </Typography>
        </Paper>

      {/* Left Panel */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        {/* Search Bar */}
        <Box
          component="input"
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value.toLowerCase())}
          placeholder={t("Search for worker name... 🔍")}
          sx={{
            flexGrow: 1,
            px: 2,
            py: 1,
            borderRadius: 2,
            border: '1px solid #ddd',
            fontSize: '0.95rem',
            outline: 'none',
            minWidth: '250px',
          }}
        />

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {/* Dropdown: Construction Sites */}
          <Box
            sx={{
              minWidth: 200,
              border: '1px solid #ccc',
              borderRadius: 2,
              px: 2,
              py: 1,
              backgroundColor: '#fff',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <FilterListIcon sx={{ fontSize: 18, color: 'gray' }} />
            <select
              value={selectedSiteId || ''}
              onChange={(e) => setSelectedSiteId(e.target.value || null)}
              style={{
                width: '100%',
                border: 'none',
                outline: 'none',
                background: 'transparent',
                fontSize: '0.95rem',
              }}
            >
              <option value="">{t("All Sites")}</option>
              {sites.map(site => (
                <option key={site.id} value={site.id}>
                  {site.name}
                </option>
              ))}
            </select>
          </Box>


          {/* Dropdown: Violation Filter */}
          <Box
            sx={{
              minWidth: 180,
              border: '1px solid #ccc',
              borderRadius: 2,
              px: 2,
              py: 1,
              backgroundColor: '#fff',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <FilterListIcon sx={{ fontSize: 18, color: 'gray' }} />
            <select
              value={violationFilter}
              onChange={(e) => setViolationFilter(e.target.value)}
              style={{
                width: '100%',
                border: 'none',
                outline: 'none',
                background: 'transparent',
                fontSize: '0.95rem',
              }}
            >
              <option value="">{t("All Violations")}</option>
              <option value="Late Clock-In">{t("Late Clock-In")}</option>
              <option value="Early Clock-Out">{t("Early Clock-Out")}</option>
              <option value="Late Clock-Out">{t("Late Clock-Out")}</option>
              <option value="Clock-In outside zone">{t("Clock-In outside zone")}</option>
              <option value="Absent">{t("Absent")}</option>
            </select>
          </Box>

        </Box>
      </Box>


        {/* Violations List */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            backgroundColor: '#ffffff',
            borderRadius: 3,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            borderRadius: 2,
            boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)"
          }}
        >
          <Typography variant="h6" fontWeight="bold">
            {t('Today\'s Clocking Violations')}
          </Typography>

        {loadingViolations ? (
          <Box textAlign="center" py={4}>
            <CircularProgress size={28} />
          </Box>
        ) : lateUsers.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t('No late clock-ins or clock-outs recorded yet.')}
          </Typography>
        ) : (
          lateUsers
            .filter(user => {
              const matchSite = selectedSiteId ? user.siteId === selectedSiteId : true;
              const matchSearch = user.name.toLowerCase().includes(searchQuery);
              const matchViolation = violationFilter
                ? user.violations.includes(violationFilter)
                : true;
              return matchSite && matchSearch && matchViolation;
            })
            .map((user, idx) => (
              <Box
                key={idx}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  borderBottom: '1px solid #eee',
                  py: 1,
                }}
              >
                {/* Left: Full Name and Site */}
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {user.name}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ flex: 1, mx: 2 }}>
                    {user.siteName}
                  </Typography>
                </Box>

                {/* Middle: Violations */}
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', flex: 1, mx: 2 }}>
                  {user.violations.map((violation, vIdx) => {
                    // Define a background color per violation type
                    const bgColorMap = {
                      'Late Clock-In': '#feefe2',
                      'Late Clock-Out': '#f8d7da',
                      'Early Clock-Out': '#FFEBEE',
                      'Clock-In outside zone': '#f3f4f9',
                      'Absent': '#e5e7eb',
                    };

                    return (
                      <Box
                        key={vIdx}
                        sx={{
                          borderRadius: 2,
                          px: 1.5,
                          py: 0.5,
                          backgroundColor: bgColorMap[violation] || '#f0f0f0',
                          fontSize: '0.85rem',
                          fontWeight: 'bold',
                          color: '#333',
                        }}
                      >
                        {t(violation)}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            ))
          )}
        </Paper>
      </Box>
    </Box>
  );
}

export default AdminAnalytics;
